<template>
  <Dialog 
    v-model:visible="visible" 
    header="Backup & Data Management" 
    :modal="true" 
    class="w-full max-w-lg bg-slate-900 border border-slate-800 text-slate-100"
  >
    <div class="space-y-5 p-2 text-slate-300 text-sm select-none">
      <!-- Export Section -->
      <div class="p-3.5 bg-slate-950 rounded-xl border border-slate-800 space-y-2">
        <h4 class="font-semibold text-slate-200 text-xs flex items-center space-x-1.5">
          <i class="pi pi-download text-purple-400"></i>
          <span>Export All Progress JSON</span>
        </h4>
        <p class="text-xs text-slate-400 leading-relaxed">
          Download a backup containing all your written code drafts, snapshot history, self-assessment checklist states, and UI preferences.
        </p>
        <Button 
          @click="$emit('export')" 
          severity="secondary" 
          size="small"
          class="!bg-slate-800 hover:!bg-slate-700 !text-slate-200 !w-full"
        >
          <i class="pi pi-file-export mr-2"></i> Download JSON Backup
        </Button>
      </div>

      <!-- Import Section -->
      <div class="p-3.5 bg-slate-950 rounded-xl border border-slate-800 space-y-2">
        <h4 class="font-semibold text-slate-200 text-xs flex items-center space-x-1.5">
          <i class="pi pi-upload text-sky-400"></i>
          <span>Import Progress JSON</span>
        </h4>
        <p class="text-xs text-slate-400 leading-relaxed">
          Restore your progress from a previously saved JSON backup file.
        </p>
        <input 
          type="file" 
          @change="handleFileChange" 
          accept=".json"
          class="text-xs text-slate-400 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-sky-600 file:text-white hover:file:bg-sky-700 cursor-pointer w-full"
        />
      </div>

      <!-- Danger Zone: Reset All -->
      <div class="p-3.5 bg-rose-950/20 border border-rose-900/40 rounded-xl space-y-2">
        <h4 class="font-semibold text-rose-400 text-xs flex items-center space-x-1.5">
          <i class="pi pi-exclamation-triangle text-rose-500"></i>
          <span>Reset All Progress Data</span>
        </h4>
        <p class="text-xs text-slate-400 leading-relaxed">
          Wipe all local IndexedDB code drafts, execution outputs, and preferences. This action cannot be undone.
        </p>
        <Button 
          @click="confirmReset" 
          severity="danger" 
          size="small"
          class="!bg-rose-700 hover:!bg-rose-800 !text-white !w-full"
        >
          <i class="pi pi-trash mr-2"></i> Reset All Data
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';

const props = defineProps<{
  visible: boolean;
}>();

const emit = defineEmits(['update:visible', 'export', 'import', 'reset-all']);

const visible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
});

function handleFileChange(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.files?.length) return;
  const file = target.files[0];
  const reader = new FileReader();
  reader.onload = () => {
    if (reader.result) {
      emit('import', reader.result as string);
    }
  };
  reader.readAsText(file);
}

function confirmReset() {
  if (confirm('⚠️ ARE YOU SURE? This will permanently delete all your saved code drafts, history, and checklist progress!')) {
    emit('reset-all');
  }
}
</script>
